import { getDatetimeText, removeEmpty } from '@back-office/ui';
import { DateRange } from '@material-ui/pickers';
import endOfDay from 'date-fns/endOfDay';
import { useLocalStore } from 'mobx-react';
import { Instance } from 'mobx-state-tree';
import qs from 'query-string';
import { MouseEvent } from 'react';
import { useMount, useToggle, useUnmount } from 'react-use';
import { Store } from '.';
import useInject from '../../../hooks/useInject';
import RecurringTransactionModel from '../../../stores/recurring-transaction.store';

type RecurringTransactionModelType = Instance<typeof RecurringTransactionModel>;
type UseViewModelReturn = {
  state: Store;
  on: boolean;
  toggle: (value: boolean) => void;
  clearFilter: () => void;
  filterMode: boolean;
  toggleFilterMode: (value: boolean) => void;
  recurringTransaction: RecurringTransactionModelType;
};

const useViewModel = (): UseViewModelReturn => {
  const { recurringTransaction, auth, getRecurringTransactionSearch } =
    useInject((root) => root);

  const [on, toggle] = useToggle(false);

  const state = useLocalStore<Store>(() => ({
    date: [null, null],
    openCalendar: false,
    start: 0,
    page: 1,
    count: 20,
    wallet_id: '',
    mobile_no: '',
    status: '',
    batch_name: '',
    reference_no: '',
    transaction_id: '',
    amount: '',
    get query(): string {
      return qs.stringify(
        removeEmpty({
          page: this.page,
          count: 20,
          merchantID: auth.currentUser.merchantID,
          wallet_id: this.wallet_id || null,
          mobile_no: this.mobile_no || null,
          batch_name: this.batch_name || null,
          reference_no: this.reference_no || null,
          transaction_id: this.transaction_id || null,
          amount: this.amount || null,
          status: this.status || null,
          start_date_from: this.date[0]
            ? getDatetimeText(this.date[0], 'json')
            : null,
          start_date_to: this.date[1]
            ? getDatetimeText(endOfDay(this.date[1]), 'json')
            : null,
        })
      );
    },
    handleOnGo() {
      this.page = 1;
      this.start = 0;
      getRecurringTransactionSearch(this.query);
    },

    handleOnChangeDatePicker(date: DateRange<Date>) {
      this.date = date;
    },

    handleOnClear(event: MouseEvent<HTMLOrSVGElement>) {
      event.stopPropagation();
      this.date = [null, null];
      this.handleOnGo();
    },
    handleOnClickDatePicker() {
      this.openCalendar = true;
    },
    handleOnCloseDatePicker() {
      this.openCalendar = false;
      this.handleOnGo();
    },

    handleOnChangePage(newPage: number) {
      this.page = newPage;
      getRecurringTransactionSearch(this.query);
    },

    handleWalletIdSearch(searchValue: string) {
      if (this.wallet_id !== searchValue) {
        this.wallet_id = searchValue;
        this.handleOnGo();
      }
    },
    handleMobileNoSearch(searchValue: string) {
      if (this.mobile_no !== searchValue) {
        this.mobile_no = searchValue;
        this.handleOnGo();
      }
    },
    handleBatchNameSearch(searchValue: string) {
      if (this.batch_name !== searchValue) {
        this.batch_name = searchValue;
        this.handleOnGo();
      }
    },
    handleReferenceNoSearch(searchValue: string) {
      if (this.reference_no !== searchValue) {
        this.reference_no = searchValue;
        this.handleOnGo();
      }
    },
    handleTransactionIdSearch(searchValue: string) {
      if (this.transaction_id !== searchValue) {
        this.transaction_id = searchValue;
        this.handleOnGo();
      }
    },
    handleAmountSearch(searchValue: string) {
      if (this.amount !== searchValue) {
        this.amount = searchValue;
        this.handleOnGo();
      }
    },
    handleStatusSearch(value: string) {
      if (this.status !== value) {
        this.status = value === 'All' ? '' : value;
        this.handleOnGo();
      }
    },
  }));

  const clearFilter = (): void => {
    state.wallet_id = '';
    state.mobile_no = '';
    state.status = '';
    state.batch_name = '';
    state.reference_no = '';
    state.transaction_id = '';
    state.amount = '';
    state.date = [null, null];
    state.handleOnGo();
  };

  useMount(() => {
    getRecurringTransactionSearch(state.query);
  });

  useUnmount(() => {
    recurringTransaction.reset();
  });

  const [filterMode, toggleFilterMode] = useToggle(false);

  return {
    state,
    on,
    toggle,
    clearFilter,
    filterMode,
    toggleFilterMode,
    recurringTransaction,
  };
};

export default useViewModel;
